import { Component, Injector } from '@angular/core';
import { IManagableObject } from "../../IManagableObject";
import { Observable } from "rxjs";
import { GroupBaseAdminPanelComponent } from "../../base-admin-panel/group-base-admin-panel.component";
import { EditDriverComponent } from "../edit-driver.component";
import { CreateDriverComponent } from "../create-driver.component";
import { DriversService } from "../../../../services/drivers.service";
import { DriversViewModel } from "../models/DriversViewModel";
import { PenaltyDialogComponent } from "../penalty-dialog/penalty-dialog.component";
import { DriverImportResultModel } from "../../../../models/DriverImportResultModel";
import { ContainersService } from "../../../../services/containers.service";
import { ContainerViewModel } from "../../ContainerViewModel";
import { GroupManagementDialogComponent } from "../../group-management-dialog/group-management-dialog.component";
import { ObjectsManagementDialogComponent } from "../../objects-management-dialog/objects-management-dialog.component";
import { ObjectGroupViewModel } from "../../ObjectGroupViewModel";
import { ContainerAcl } from 'app/models/ContainerAcl';
import { matchesSearchRule } from "../../../shared/pipes/filterByProperty";


@Component({
  selector: '[app-drivers]',
  templateUrl: './drivers.component.html',
  styleUrls: ['./drivers.component.css'],
  standalone: false
})
export class AdminDriversComponent extends GroupBaseAdminPanelComponent {
  private containersService: ContainersService;
  public containerFilterId: number = 0;
  public containers: ContainerViewModel[] = [];
  private containerNameById = new Map<number, string>();
  private containerAclById = new Map<number, number>();
  canAddDriverGroup: boolean;
  constructor(private service: DriversService, protected injector: Injector) {
    super(injector);
    this.containersService = injector.get(ContainersService);
    this.groupName = "DriversGroup";
    this.entityName = "Driver";
  }

  protected Refresh() {
    this.loadContainers();

    this.service.GetAllDrivers().subscribe(drivers => {
      this.objects = drivers;
    });

    this.service.GetGroups().subscribe(groups => {
      this.groupFilter.groups = groups;
    });
  }

  public get filteredDrivers(): DriversViewModel[] {
    let items = this.objects as DriversViewModel[];

    const term = this.filter.term ?? "";
    if (term) {
      items = items.filter(driver =>
        matchesSearchRule(driver.Name, term));
    }

    if (this.filter.group?.Id > 0) {
      const groupIds = new Set(this.filter.group.objects ?? []);
      items = items.filter(driver => groupIds.has(driver.Id));
    }

    if (this.containerFilterId > 0)
      items = items.filter(driver => driver.ContainerId === this.containerFilterId);

    return items;
  }

  public getContainerName(containerId?: number): string {
    if (!containerId)
      return "";

    return this.containerNameById.get(containerId) ?? "";
  }

  public openEditModal(driver: DriversViewModel) {
    this.prepareModal(EditDriverComponent);
    this.componentRef.instance.open(driver);
  }

  public openCreateModal() {
    this.prepareModal(CreateDriverComponent);
    this.componentRef.instance.open(new DriversViewModel());
  }

  public openPenaltyModal() {
    this.service.GetPenalty().subscribe(driver => {
      this.prepareModal(PenaltyDialogComponent);
      this.componentRef.instance.open(driver);
    });
  }

  protected doCreateGroup(input: IManagableObject): Observable<any> {
    return this.service.CreateGroup(input);
  }

  protected doDeleteGroup(input: IManagableObject): Observable<any> {
    return this.service.DeleteGroup(input);
  }

  protected doDeleteObject(id: number): Observable<any> {
    return this.service.DeleteObject(id);
  }

  public override addGroup() {
    if (!this.containers.length) return;

    this.groupMgmtDialog = this.dialogService.open(this.groupsContainer, GroupManagementDialogComponent);
    this.groupMgmtDialog.instance.containers = this.containers;
    this.groupMgmtDialog.instance.readonlyContainer = false;
    this.initDriverGroupDialog();

    const group = new ObjectGroupViewModel();
    group.ContainerId = this.containers[0].Id;
    this.groupMgmtDialog.instance.open(group);
  }

  public override editGroup(group: ObjectGroupViewModel) {
    this.groupMgmtDialog = this.dialogService.open(this.groupsContainer, GroupManagementDialogComponent);
    this.groupMgmtDialog.instance.containers = this.containers ?? [];
    this.groupMgmtDialog.instance.readonlyContainer = true;
    this.initDriverGroupDialog();
    this.groupMgmtDialog.instance.open(group);
  }

  importDrivers(event) {
    let file = event.target.files[0];
    const importFailedMsg = this.translate.instant("Import failed");
    const importSuccessMsg = this.translate.instant("Imported");
    const keysConflictMsg = this.translate.instant("Key duplicates");

    if (file) {
      this.service.importFromFile(file)
        .subscribe({
          next: (response: DriverImportResultModel) => {
            this.toastr.success(`${importSuccessMsg}: ${response.SuccessDriversCount}`);
            if (response.DriverKeyUidDuplicates.length > 0)
              this.toastr.error(`${keysConflictMsg}: ${response.DriverKeyUidDuplicates.length}`);
            if (response.DriverNameDuplicates.length > 0)
              this.toastr.error(`${importFailedMsg}: ${response.DriverNameDuplicates.length}`);
            this.Refresh();
          },
          error: () => {
            this.toastr.error(importFailedMsg);
          }
        });
    }
  }

  private loadContainers() {
    const currentUser = this.usersService.getCurrentUser();
    if (!currentUser) {
      this.containers = [];
      this.containerNameById = new Map<number, string>();
      this.containerFilterId = 0;
      this.buildContainerAclMap();
      this.canAddDriverGroup = false;
      return;
    }

    this.containersService.GetContainers().subscribe(containers => {
      this.containers = [...(containers ?? [])]
        .sort((a, b) => (a.Name ?? "").localeCompare(b.Name ?? ""));
      this.containerNameById = new Map(this.containers.map(container => [container.Id, container.Name] as [number, string]));
      if (this.containerFilterId > 0 && !this.containers.some(container => container.Id === this.containerFilterId))
        this.containerFilterId = 0;
      this.buildContainerAclMap();
      this.canAddDriverGroup = this.getCreatableContainers(this.containers).length > 0;
    });
  }

  private getCreatableContainers(containers: ContainerViewModel[]): ContainerViewModel[] {
    return containers.filter(container =>
      (this.getContainerAcl(container) & ContainerAcl.CreateDriver) === ContainerAcl.CreateDriver);
  }

  private initDriverGroupDialog() {
    const allObjects = this.objects.map(x => Object.assign({}, x)) as DriversViewModel[];

    this.groupMgmtDialog.instance.onSubmit.subscribe(() => {
      const input = this.groupMgmtDialog.instance.object as ObjectGroupViewModel;
      input.objects = this.groupMgmtDialog.instance.objects.map(driver => driver.Id);
      this.doCreateGroup(input).subscribe(() => {
        if (this.groupName)
          this.syncService.NotifyEntityChanged(this.groupName, input.Name);
        this.Refresh();
      });
      this.groupMgmtDialog.instance.close();
    });

    this.groupMgmtDialog.instance.onOpen.subscribe(() => {
      const group = this.groupMgmtDialog.instance.object as ObjectGroupViewModel;
      this.groupMgmtDialog.instance.objects = this.getVisibleGroupDrivers(allObjects, group.ContainerId, group.objects)
        .filter(driver => (group.objects ?? []).indexOf(driver.Id) >= 0)
        .map(driver => {
          driver.IsChecked = false;
          return driver;
        });
    });

    this.groupMgmtDialog.instance.onAddObjects.subscribe(() => {
      this.objectsListDialog = this.dialogService.open(this.objectsListContainer, ObjectsManagementDialogComponent);
      this.objectsListDialog.instance.onOpen.subscribe(() => {
        const group = this.groupMgmtDialog.instance.object as ObjectGroupViewModel;
        const filteredObjects = this.getVisibleGroupDrivers(allObjects, group.ContainerId, group.objects)
          .filter(driver => this.groupMgmtDialog.instance.objects.find(existing => existing.Id == driver.Id) == undefined)
          .map(x => Object.assign({}, x))
          .map(driver => {
            driver.IsChecked = false;
            return driver;
          });

        this.objectsListDialog.instance.groups = this.groupFilter.groups;
        this.objectsListDialog.instance.objects = filteredObjects;
      });

      this.objectsListDialog.instance.onSubmit.subscribe(() => {
        this.objectsListDialog.instance.objects.forEach(driver => {
          if (driver && driver.IsChecked) {
            if (this.groupMgmtDialog.instance.objects.indexOf(driver) < 0) {
              driver.IsChecked = false;
              this.groupMgmtDialog.instance.objects.push(driver);
            }
          }
        });
        this.objectsListDialog.instance.close();
      });

      this.objectsListDialog.instance.open();
    });
  }

  private getVisibleGroupDrivers(allObjects: DriversViewModel[], containerId?: number, selectedIds?: number[]): DriversViewModel[] {
    const ids = selectedIds ?? [];
    return allObjects.filter(driver => {
      if (ids.includes(driver.Id))
        return true;

      if (!containerId)
        return false;

      return driver.ContainerId === containerId;
    });
  }


  public canAddDriver(): boolean {
    const user = this.usersService.getCurrentUser();
    if (!user) return false;

    if (user.CanDo(this.AccessRights.EDITUSERS) || user.CanDo(this.AccessRights.EDIT_ALL_USERS))
      return true;

    for (const acl of this.containerAclById.values()) {
      if ((acl & ContainerAcl.CreateDriver) === ContainerAcl.CreateDriver)
        return true;
    }

    return false;
  }
  public canEditDriver(driver: DriversViewModel): boolean {
    return this.hasDriverPermission(driver, ContainerAcl.EditDriver);
  }

  public canDeleteDriver(DriversViewModel: DriversViewModel): boolean {
    return this.hasDriverPermission(DriversViewModel, ContainerAcl.DeleteDriver);
  }

  private hasDriverPermission(key: DriversViewModel, permission: ContainerAcl): boolean {
    const user = this.usersService.getCurrentUser();
    if (!user) return false;

    if (user.CanDo(this.AccessRights.EDITUSERS) || user.CanDo(this.AccessRights.EDIT_ALL_USERS))
      return true;

    if (!key?.ContainerId)
      return false;

    const acl = this.containerAclById.get(key.ContainerId) ?? 0;
    return (acl & permission) === permission;
  }

  private buildContainerAclMap() {
    this.containerAclById = new Map<number, number>();

    for (const container of this.containers) {
      this.containerAclById.set(container.Id, this.getContainerAcl(container));
    }
  }

  private getContainerAcl(container: ContainerViewModel): number {
    const currentUser = this.usersService.getCurrentUser();
    if (!currentUser)
      return 0;

    if (currentUser.CanDo(this.AccessRights.EDITUSERS) || currentUser.CanDo(this.AccessRights.EDIT_ALL_USERS))
      return ContainerAcl.CreateDriver | ContainerAcl.EditDriver | ContainerAcl.DeleteDriver;

    const userId = currentUser.Id;
    const aclFromUsers = container.Users?.find(link => link.UserId === userId)?.ACL;
    const fallbackAcl = (currentUser.ContainerLinks ?? []).find(link => link.ContainerId === container.Id)?.ACL;
    return aclFromUsers ?? fallbackAcl ?? 0;
  }
}
